import { Link } from "react-router-dom";

/**
 * Compact page-specific extended SEO content used at the bottom of
 * key sub-pages (Common Number, Previous Result, Dream Number, etc).
 * Renders natural prose that covers relevant long-tail queries.
 */
export default function PageSeoNote({ topic }) {
  if (topic === "common-number") {
    return (
      <section className="max-w-xl mx-auto w-full px-4 mt-8 pt-6 border-t border-slate-200 text-[15px] text-slate-700 leading-relaxed space-y-3">
        <h2 className="font-heading font-black text-slate-900 text-lg sm:text-xl">
          About the Morning Teer Common Number
        </h2>
        <p>
          The <strong>Morning Teer Common Number</strong> is one of the most searched Teer
          topics in Meghalaya. On this page you can view today's <strong>Direct</strong>,{" "}
          <strong>House</strong> and <strong>Ending</strong> picks for both the F/R and S/R
          rounds of the Shillong Morning Teer draw. Refresh the page after 10:30 AM Asia/Kolkata
          time to see the freshest picks.
        </p>
        <p>
          Explore related research pages:{" "}
          <Link to="/target-number" className="text-sky-700 font-semibold underline">
            Morning Teer Target Number
          </Link>{" "}
          ·{" "}
          <Link to="/previous-results" className="text-sky-700 font-semibold underline">
            Morning Teer Previous Result
          </Link>{" "}
          ·{" "}
          <Link to="/result-chart" className="text-sky-700 font-semibold underline">
            Shillong Morning Teer Result Chart
          </Link>
          . Please remember: House digits, Ending digits and Direct picks are shared as a study
          reference only. They are <strong>not guaranteed</strong>.
        </p>
      </section>
    );
  }

  if (topic === "previous-results") {
    return (
      <section className="max-w-xl mx-auto w-full px-4 mt-8 pt-6 border-t border-slate-200 text-[15px] text-slate-700 leading-relaxed space-y-3">
        <h2 className="font-heading font-black text-slate-900 text-lg sm:text-xl">
          About Morning Teer Previous Results
        </h2>
        <p>
          This archive is useful for anyone researching the{" "}
          <strong>Shillong Teer Morning Result</strong>, the{" "}
          <strong>Morning Teer Result Chart</strong>, or a long-term{" "}
          <strong>Morning Teer Result List</strong>. Use the date filter to pick a specific window
          such as the last 30 days or the last month — the same data can then be viewed as a
          monthly grid on the{" "}
          <Link to="/result-chart" className="text-sky-700 font-semibold underline">
            Result Chart
          </Link>{" "}
          page.
        </p>
        <p>
          Many players cross-check the{" "}
          <Link to="/common-number" className="text-sky-700 font-semibold underline">
            Common Number
          </Link>{" "}
          and{" "}
          <Link to="/target-number" className="text-sky-700 font-semibold underline">
            Target Number
          </Link>{" "}
          against the previous week's actual F/R and S/R numbers before the next draw. All
          numbers shown here reflect the announcement made from the counter. Always verify with a
          reliable source before making any decision — this is a community information portal,
          not an official counter website.
        </p>
      </section>
    );
  }

  if (topic === "dream-number") {
    return (
      <section className="max-w-xl mx-auto w-full px-4 mt-8 pt-6 border-t border-slate-200 text-[15px] text-slate-700 leading-relaxed space-y-3">
        <h2 className="font-heading font-black text-slate-900 text-lg sm:text-xl">
          About Morning Teer Dream Numbers
        </h2>
        <p>
          A <strong>Dream Number</strong> is a traditional keyword-to-number mapping that many
          Teer followers keep as a personal chart. Users searching for the{" "}
          <strong>Morning Teer Dream Number</strong> or the{" "}
          <strong>Morning Teer Lucky Number Today</strong> can type a dream word here —
          water, fish, snake, wedding, money, elephant and more — to see the traditionally
          associated two-digit number.
        </p>
        <p>
          Dream Numbers are community folklore, not a prediction system. Pair them with the{" "}
          <Link to="/common-number" className="text-sky-700 font-semibold underline">
            Common Number
          </Link>{" "}
          and{" "}
          <Link to="/previous-results" className="text-sky-700 font-semibold underline">
            Previous Result
          </Link>{" "}
          pages for a fuller picture, and always cross-check the final number from a reliable
          local source.
        </p>
      </section>
    );
  }

  if (topic === "target-number") {
    return (
      <section className="max-w-xl mx-auto w-full px-4 mt-8 pt-6 border-t border-slate-200 text-[15px] text-slate-700 leading-relaxed space-y-3">
        <h2 className="font-heading font-black text-slate-900 text-lg sm:text-xl">
          About Morning Teer Target Numbers
        </h2>
        <p>
          The <strong>Morning Teer Target Number</strong> is a shortlist of two-digit picks
          derived from the most frequent House and Ending digits of recent draws. The same
          calculation is applied consistently to give a fresh set of picks before every draw.
        </p>
        <p>
          Compare with the{" "}
          <Link to="/common-number" className="text-sky-700 font-semibold underline">
            Common Number
          </Link>{" "}
          or study the{" "}
          <Link to="/result-chart" className="text-sky-700 font-semibold underline">
            Result Chart
          </Link>{" "}
          for context. Suggestions here are statistical study references — no Teer number can be
          predicted with certainty, and this website makes no guarantee.
        </p>
      </section>
    );
  }

  return null;
}
